import { TypedDocumentNode, gql } from '@apollo/client';
import { ComponentProps } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandS, BrandXXS, LabelM } from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { Variant as NewVariant } from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { Avatar } from '@sorare/core/src/components/user/Avatar';
import { uniqueBy } from '@sorare/core/src/lib/arrays';
import { playerDetails } from '@sorare/core/src/lib/glossary';
import { WithRewardRange } from '@sorare/core/src/lib/rewards';

import { RankRewardTitle } from 'components/leaderboard/CumulativeRankRewards/RankRewardTitle';
import {
  RankRow,
  getRankRowState,
} from 'components/leaderboard/CumulativeRankRewards/RankRow';
import {
  RewardRange,
  RewardRangeVariant,
} from 'components/leaderboard/RewardRange';
import { ClickableAnyRewardConfig } from 'components/rewards/ClickableAnyRewardConfig';

import type {
  RankReward_so5Leaderboard,
  RankReward_so5Ranking,
  RankReward_so5RewardConfig,
} from './__generated__/index.graphql';

const AvatarContainer = styled(Horizontal).attrs({ gap: 0, center: true })`
  --avatar-size: 40px;
  width: var(--avatar-size);
  height: var(--avatar-size);
  border-radius: 50%;
  background-color: var(--c-nd-400);

  & > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

type Props = {
  so5Leaderboard: RankReward_so5Leaderboard;
  so5RewardConfig: WithRewardRange<RankReward_so5RewardConfig>;
  so5Ranking?: RankReward_so5Ranking;
  overview: boolean;
  isMyRange: boolean;
  isMyNextRange: boolean;
  rewardRangeVariant: RewardRangeVariant;
  rewardVariant?: NewVariant;
  onClickRanking?: (so5RankingId: string) => void;
  hideRankings?: boolean;
  selectedContender?: string;
  hideRewards?: boolean;
  hideScoreDistance?: boolean;
} & Pick<ComponentProps<typeof RewardRange>, 'withSeparator'>;

export const RankReward = ({
  so5Leaderboard,
  so5RewardConfig,
  so5Ranking,
  overview,
  isMyRange,
  isMyNextRange,
  rewardRangeVariant,
  onClickRanking,
  hideRankings,
  withSeparator,
  rewardVariant,
  selectedContender,
  hideRewards,
  hideScoreDistance,
}: Props) => {
  const isMyRangeOrMyNextRange = isMyRange || isMyNextRange;
  const shouldDisplay = overview ? isMyRangeOrMyNextRange : true;

  if (!shouldDisplay) {
    return null;
  }

  const scoreDistanceFromMyRanking = so5Ranking
    ? (so5Ranking.score || 0) - (so5RewardConfig.toSo5Ranking?.score || 0)
    : undefined;

  return (
    <RewardRange
      key={so5RewardConfig.startRank}
      variant={rewardRangeVariant}
      title={
        <RankRewardTitle
          {...so5RewardConfig}
          scoreDistanceFromMyRanking={scoreDistanceFromMyRanking}
          hideScoreDistance={hideScoreDistance}
        />
      }
      rewards={
        hideRewards
          ? []
          : so5RewardConfig.rewardConfigs.map((rewardConfig, i) => {
              return (
                <ClickableAnyRewardConfig
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                  anyRewardConfig={rewardConfig}
                  so5League={so5Leaderboard.so5League}
                  variant={rewardVariant}
                />
              );
            })
      }
      withSeparator={withSeparator}
    >
      {!hideRankings &&
        uniqueBy(
          ranking => (ranking.ranking || 0).toString(),
          [
            so5RewardConfig.fromSo5Ranking,
            isMyRange && so5Ranking,
            so5RewardConfig.toSo5Ranking,
          ].filter(Boolean)
        ).map(ranking => {
          return (
            <ButtonBase
              key={ranking.ranking}
              style={{ textAlign: 'left' }}
              onClick={() =>
                onClickRanking?.(ranking.so5Lineup.so5LeaderboardContender.slug)
              }
            >
              <RankRow
                state={getRankRowState(
                  ranking.ranking === so5Ranking?.ranking,
                  selectedContender ===
                    ranking?.so5Lineup.so5LeaderboardContender.slug,
                  ranking.ranking
                )}
                rank={ranking.ranking}
              >
                <Horizontal gap={1.5}>
                  <AvatarContainer>
                    <Avatar user={ranking.so5Lineup.user} size={5} />
                  </AvatarContainer>
                  <Vertical gap={0}>
                    <LabelM bold>
                      <Ellipsis>{ranking.so5Lineup.user.nickname}</Ellipsis>
                    </LabelM>
                    <Horizontal gap={0.5}>
                      <BrandS as="span" color="var(--c-white)">
                        <FormattedNumber value={ranking.score} />
                      </BrandS>
                      <BrandXXS color="var(--c-nd-600)" as="span">
                        <FormattedMessage {...playerDetails.points} />
                      </BrandXXS>
                    </Horizontal>
                  </Vertical>
                </Horizontal>
              </RankRow>
            </ButtonBase>
          );
        })}
    </RewardRange>
  );
};
RankReward.fragments = {
  so5Leaderboard: gql`
    fragment RankReward_so5Leaderboard on So5Leaderboard {
      slug
      mainRarityType
      so5League {
        slug
        ...ClickableAnyRewardConfig_so5League
      }
    }
    ${ClickableAnyRewardConfig.fragments.so5League}
  ` as TypedDocumentNode<RankReward_so5Leaderboard>,
  so5RewardConfig: gql`
    fragment RankReward_so5RewardConfig on So5RewardConfig {
      id
      ranks
      rankPct
      rewardConfigs {
        id
        ...ClickableAnyRewardConfig_anyRewardConfigInterface
      }
      fromSo5Ranking {
        id
        ranking
        score
        so5Lineup {
          id
          user {
            slug
            nickname
            ...Avatar_user
          }
          so5LeaderboardContender {
            slug
          }
        }
      }
      toSo5Ranking {
        id
        ranking
        score
        so5Lineup {
          id
          user {
            slug
            nickname
            ...Avatar_user
          }
          so5LeaderboardContender {
            slug
          }
        }
      }
    }
    ${ClickableAnyRewardConfig.fragments.anyRewardConfigInterface}
    ${Avatar.fragments.user}
  ` as TypedDocumentNode<RankReward_so5RewardConfig>,
  so5Ranking: gql`
    fragment RankReward_so5Ranking on So5Ranking {
      id
      ranking
      score
      so5Lineup {
        id
        user {
          slug
          nickname
          ...Avatar_user
        }
        so5LeaderboardContender {
          slug
        }
      }
    }
  ` as TypedDocumentNode<RankReward_so5Ranking>,
};
